import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { SearchLg, LayoutGrid01, List, Edit01, Move, Share01, Trash01, Folder, Plus, Download01 } from "@untitledui/icons";
import { Input } from "@/components/base/input/input";
import { Tabs } from "@/components/application/tabs/tabs";
import { DocumentList } from "@/components/application/documents/document-list";
import { UnmappedFilesList } from "@/components/application/documents/unmapped-files-list";
import { Button } from "@/components/base/buttons/button";
import { documentService } from "@/data/mock-documents";
import { containerService } from "@/data/mock-containers";
import { customFolderService } from "@/data/mock-custom-folders";
import type { DocumentFile, UnmappedFile, ViewMode, BreadcrumbItem } from "@/types/documents";
import type { Container } from "@/types/container";
import type { CustomFolder } from "@/types/documents";
import { useTranslation } from "react-i18next";
import { ChevronRight } from "@untitledui/icons";
import { ContextMenu, type ContextMenuItem } from "@/components/application/documents/context-menu";
import { Modal, ModalOverlay, Dialog } from "@/components/application/modals/modal";
import { Select } from "@/components/base/select/select";
import { Toggle } from "@/components/base/toggle/toggle";
import { Dropdown } from "@/components/base/dropdown/dropdown";
import { FileUploadDropZone } from "@/components/application/file-upload/file-upload-base";

export const ShipperDocumentsPage = () => {
    const { t } = useTranslation("pages:documents");
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();

    const [activeTab, setActiveTab] = useState<"myDrive" | "unmapped">(
        (searchParams.get("tab") as "myDrive" | "unmapped") || "myDrive"
    );
    const [viewMode, setViewMode] = useState<ViewMode>(
        (searchParams.get("view") as ViewMode) || "card"
    );
    const [showEmptyFolders, setShowEmptyFolders] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [searchResults, setSearchResults] = useState<Array<{
        type: "document" | "unmapped_file" | "container" | "custom_folder";
        item: DocumentFile | UnmappedFile | Container | CustomFolder;
    }>>([]);
    const [isSearching, setIsSearching] = useState(false);

    // Navigation state
    const [currentFolderId, setCurrentFolderId] = useState<string | null>(
        searchParams.get("folder") || null
    );
    const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbItem[]>([]);

    // Data
    const [containers, setContainers] = useState<Container[]>([]);
    const [customFolders, setCustomFolders] = useState<CustomFolder[]>([]);
    const [documents, setDocuments] = useState<DocumentFile[]>([]);
    const [unmappedFiles, setUnmappedFiles] = useState<UnmappedFile[]>([]);

    // Context Menu state
    const [contextMenu, setContextMenu] = useState<{
        isOpen: boolean;
        position: { x: number; y: number } | null;
        items: ContextMenuItem[];
    }>({
        isOpen: false,
        position: null,
        items: [],
    });

    // Modals state
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showMoveModal, setShowMoveModal] = useState(false);
    const [showMoveFolderModal, setShowMoveFolderModal] = useState(false);
    const [showShareModal, setShowShareModal] = useState(false);
    const [showDownloadModal, setShowDownloadModal] = useState(false);
    const [showCreateFolderModal, setShowCreateFolderModal] = useState(false);
    const [selectedDocument, setSelectedDocument] = useState<DocumentFile | null>(null);
    const [selectedCustomFolder, setSelectedCustomFolder] = useState<CustomFolder | null>(null);
    const [renameValue, setRenameValue] = useState("");
    const [newFolderName, setNewFolderName] = useState("");
    const [selectedContainerId, setSelectedContainerId] = useState("");
    const [selectedParentFolderId, setSelectedParentFolderId] = useState<string | null>(null);
    const [selectedContainer, setSelectedContainer] = useState<Container | null>(null);

    // Load data based on current folder
    useEffect(() => {
        const accountId = localStorage.getItem("accountId") || "1";
        const shipperContainers = containerService.getByShipperId(accountId);

        if (activeTab === "myDrive") {
            if (currentFolderId) {
                // Navigate into folder
                const folder = customFolderService.getById(currentFolderId);
                if (folder) {
                    // Show containers in this folder (and any nested custom folders)
                    const folderContainers = folder.containerIds
                        .map(id => containerService.getById(id))
                        .filter((c): c is Container => c !== undefined);
                    setContainers(folderContainers);

                    // Show nested custom folders
                    const nestedFolders = customFolderService.getByParentId(currentFolderId);
                    setCustomFolders(nestedFolders);

                    // Don't show documents directly in custom folders - they are only inside container folders
                    setDocuments([]);

                    // Build breadcrumbs
                    const path = customFolderService.getFolderPath(currentFolderId);
                    setBreadcrumbs([
                        { id: "root", name: t("drive.breadcrumb.home"), type: "root" },
                        ...path.map(f => ({
                            id: f.id,
                            name: f.name,
                            type: "custom_folder" as const,
                        })),
                    ]);
                } else {
                    // Container folder
                    const container = containerService.getById(currentFolderId);
                    if (container) {
                        setContainers([]);
                        setCustomFolders([]);
                        const containerDocs = documentService.getByContainerId(currentFolderId) as DocumentFile[];
                        setDocuments(containerDocs);

                        // Find which custom folder(s) contain this container
                        const containingFolders = customFolderService.getFoldersContainingContainer(currentFolderId);

                        if (containingFolders.length > 0) {
                            // Il container è dentro una o più custom folders
                            // Prendiamo la cartella più annidata (quella con il path più lungo)
                            // per avere il breadcrumb completo
                            const parentFolder = containingFolders.reduce((deepest, current) => {
                                const deepestPath = customFolderService.getFolderPath(deepest.id);
                                const currentPath = customFolderService.getFolderPath(current.id);
                                return currentPath.length > deepestPath.length ? current : deepest;
                            });
                            const folderPath = customFolderService.getFolderPath(parentFolder.id);

                            setBreadcrumbs([
                                { id: "root", name: t("drive.breadcrumb.home"), type: "root" },
                                ...folderPath.map(f => ({
                                    id: f.id,
                                    name: f.name,
                                    type: "custom_folder" as const,
                                })),
                                { id: container.id, name: container.containerNumber, type: "container" },
                            ]);
                        } else {
                            // Il container non è in nessuna custom folder, è alla root
                            setBreadcrumbs([
                                { id: "root", name: t("drive.breadcrumb.home"), type: "root" },
                                { id: container.id, name: container.containerNumber, type: "container" },
                            ]);
                        }
                    }
                }
            } else {
                // Root level: show containers and custom folders
                setContainers(shipperContainers);
                setCustomFolders(customFolderService.getRootFolders());
                setDocuments([]);
                setBreadcrumbs([{ id: "root", name: t("drive.breadcrumb.home"), type: "root" }]);
            }
        } else {
            // Unmapped files tab
            setUnmappedFiles(documentService.getUnmappedFiles());
            setContainers([]);
            setCustomFolders([]);
            setDocuments([]);
            setBreadcrumbs([]);
        }
    }, [activeTab, currentFolderId, t]);

    // Handle search
    const handleSearch = (query: string) => {
        if (!query.trim()) {
            setIsSearching(false);
            setSearchResults([]);
            return;
        }

        setIsSearching(true);
        const results = documentService.globalSearch(query);
        setSearchResults(results);
    };

    // Debounce search with automatic search on query change
    useEffect(() => {
        const timeoutId = setTimeout(() => {
            if (!searchQuery.trim()) {
                setIsSearching(false);
                setSearchResults([]);
            } else {
                handleSearch(searchQuery);
            }
        }, 300);

        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    const handleSearchKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === "Enter") {
            handleSearch(searchQuery);
        }
    };

    // Navigation handlers
    const handleContainerClick = (container: Container) => {
        setCurrentFolderId(container.id);
        setSearchParams({ tab: "myDrive", folder: container.id, view: viewMode });
    };

    const handleCustomFolderClick = (folder: CustomFolder) => {
        setCurrentFolderId(folder.id);
        setSearchParams({ tab: "myDrive", folder: folder.id, view: viewMode });
    };

    const handleDocumentClick = (document: DocumentFile) => {
        navigate(`/shipper/documents/${document.id}`);
    };

    const handleBreadcrumbClick = (item: BreadcrumbItem) => {
        if (item.type === "root") {
            setCurrentFolderId(null);
            setSearchParams({ tab: "myDrive", view: viewMode });
        } else {
            setCurrentFolderId(item.id);
            setSearchParams({ tab: "myDrive", folder: item.id, view: viewMode });
        }
    };

    // Context Menu handlers
    const handleDocumentContextMenu = (document: DocumentFile, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "download",
                label: t("drive.document.actions.download"),
                icon: Download01,
                onAction: () => {
                    setSelectedDocument(document);
                    setShowDownloadModal(true);
                },
            },
            {
                id: "rename",
                label: t("drive.document.actions.rename"),
                icon: Edit01,
                onAction: () => {
                    setSelectedDocument(document);
                    setRenameValue(document.name);
                    setShowRenameModal(true);
                },
            },
            {
                id: "move",
                label: t("drive.document.actions.move"),
                icon: Move,
                onAction: () => {
                    setSelectedDocument(document);
                    setSelectedContainerId(document.containerId || "");
                    setShowMoveModal(true);
                },
            },
            {
                id: "share",
                label: t("drive.document.actions.share"),
                icon: Share01,
                onAction: () => {
                    setSelectedDocument(document);
                    setShowShareModal(true);
                },
            },
            {
                id: "delete",
                label: t("drive.document.actions.delete"),
                icon: Trash01,
                isDestructive: true,
                onAction: () => {
                    setSelectedDocument(document);
                    setShowDeleteModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    const handleContainerContextMenu = (container: Container, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "open",
                label: t("drive.folder.open"),
                icon: Folder,
                onAction: () => {
                    handleContainerClick(container);
                },
            },
            {
                id: "move",
                label: t("drive.folder.moveAction"),
                icon: Move,
                onAction: () => {
                    setSelectedContainer(container);
                    setSelectedParentFolderId(null);
                    setShowMoveFolderModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    const handleCustomFolderContextMenu = (folder: CustomFolder, e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "open",
                label: t("drive.folder.open"),
                icon: Folder,
                onAction: () => {
                    handleCustomFolderClick(folder);
                },
            },
            {
                id: "rename",
                label: t("drive.folder.rename"),
                icon: Edit01,
                onAction: () => {
                    setSelectedCustomFolder(folder);
                    setRenameValue(folder.name);
                    setShowRenameModal(true);
                },
            },
            {
                id: "move",
                label: t("drive.folder.moveAction"),
                icon: Move,
                onAction: () => {
                    setSelectedCustomFolder(folder);
                    setSelectedParentFolderId(folder.parentId);
                    setShowMoveFolderModal(true);
                },
            },
            {
                id: "delete",
                label: t("drive.folder.delete"),
                icon: Trash01,
                isDestructive: true,
                onAction: () => {
                    setSelectedCustomFolder(folder);
                    setShowDeleteModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    const closeContextMenu = () => {
        setContextMenu({
            isOpen: false,
            position: null,
            items: [],
        });
    };

    // Generic context menu handler (for empty area)
    const handleGenericContextMenu = (e: React.MouseEvent) => {
        // Don't show context menu during search
        if (isSearching) return;

        // Only show if clicking on empty area (not on a file/folder)
        const target = e.target as HTMLElement;
        if (target.closest('[data-document-item]') || target.closest('[data-folder-item]')) {
            return; // Don't show generic menu if clicking on item
        }

        e.preventDefault();
        e.stopPropagation();

        const items: ContextMenuItem[] = [
            {
                id: "createFolder",
                label: t("drive.folder.create"),
                icon: Folder,
                onAction: () => {
                    setNewFolderName("");
                    setSelectedParentFolderId(currentFolderId);
                    setShowCreateFolderModal(true);
                },
            },
        ];

        setContextMenu({
            isOpen: true,
            position: { x: e.clientX, y: e.clientY },
            items,
        });
    };

    // Modal handlers
    const handleRename = () => {
        if (selectedDocument && renameValue.trim()) {
            documentService.rename(selectedDocument.id, renameValue.trim());
            setShowRenameModal(false);
            setSelectedDocument(null);
            setRenameValue("");
            // Refresh data
            if (!currentFolderId) {
                setCustomFolders(customFolderService.getRootFolders());
            }
        } else if (selectedCustomFolder && renameValue.trim()) {
            customFolderService.update(selectedCustomFolder.id, { name: renameValue.trim() });
            setShowRenameModal(false);
            setSelectedCustomFolder(null);
            setRenameValue("");
            // Refresh data
            if (!currentFolderId) {
                setCustomFolders(customFolderService.getRootFolders());
            }
        }
    };

    const handleDelete = () => {
        if (selectedDocument) {
            documentService.delete(selectedDocument.id);
            setShowDeleteModal(false);
            setSelectedDocument(null);
            navigate("/shipper/documents");
        } else if (selectedCustomFolder) {
            const deleted = customFolderService.delete(selectedCustomFolder.id);
            if (deleted) {
                setShowDeleteModal(false);
                setSelectedCustomFolder(null);
                // Refresh data
                if (!currentFolderId) {
                    setCustomFolders(customFolderService.getRootFolders());
                }
                // Se stiamo navigando nella cartella eliminata, torna alla root
                if (currentFolderId === selectedCustomFolder.id) {
                    setCurrentFolderId(null);
                    setSearchParams({ tab: "myDrive", view: viewMode });
                }
            }
        }
    };

    const handleMove = () => {
        if (selectedDocument && selectedContainerId) {
            documentService.move(selectedDocument.id, selectedContainerId);
            setShowMoveModal(false);
            setSelectedDocument(null);
            setSelectedContainerId("");
            navigate("/shipper/documents");
        }
    };

    const handleShare = () => {
        if (selectedDocument) {
            documentService.share(selectedDocument.id, {
                email: "",
                permission: "view",
            });
            setShowShareModal(false);
            setSelectedDocument(null);
        }
    };

    const handleDownload = () => {
        if (selectedDocument) {
            // Simula download
            const link = window.document.createElement("a");
            link.href = selectedDocument.fileUrl;
            link.download = selectedDocument.name;
            link.click();
            setShowDownloadModal(false);
            setSelectedDocument(null);
        }
    };

    const handleMoveFolder = () => {
        if (selectedCustomFolder !== null) {
            customFolderService.update(selectedCustomFolder.id, { parentId: selectedParentFolderId });
            setShowMoveFolderModal(false);
            setSelectedCustomFolder(null);
            setSelectedParentFolderId(null);
            // Refresh data
            if (!currentFolderId) {
                setCustomFolders(customFolderService.getRootFolders());
            }
        } else if (selectedContainer !== null) {
            // Move container to custom folder
            if (selectedParentFolderId) {
                const folder = customFolderService.getById(selectedParentFolderId);
                if (folder && !folder.containerIds.includes(selectedContainer.id)) {
                    customFolderService.addContainer(selectedParentFolderId, selectedContainer.id);
                }
            } else {
                // Remove container from all folders (move to root)
                const allFolders = customFolderService.getAll();
                allFolders.forEach(f => {
                    if (f.containerIds.includes(selectedContainer.id)) {
                        customFolderService.removeContainer(f.id, selectedContainer.id);
                    }
                });
            }
            setShowMoveFolderModal(false);
            setSelectedContainer(null);
            setSelectedParentFolderId(null);
            // Refresh data
            if (!currentFolderId) {
                setCustomFolders(customFolderService.getRootFolders());
            }
        }
    };

    const handleCreateFolder = () => {
        if (!newFolderName.trim()) return;
        customFolderService.create({
            name: newFolderName.trim(),
            parentId: selectedParentFolderId,
            containerIds: [],
        });
        setShowCreateFolderModal(false);
        setNewFolderName("");
        setSelectedParentFolderId(null);
        // Refresh data
        if (!currentFolderId) {
            setCustomFolders(customFolderService.getRootFolders());
        } else {
            // Se siamo dentro una cartella, ricarica i dati
            const folder = customFolderService.getById(currentFolderId);
            if (folder) {
                const folderContainers = folder.containerIds
                    .map(id => containerService.getById(id))
                    .filter((c): c is Container => c !== undefined);
                setContainers(folderContainers);
                setCustomFolders(customFolderService.getByParentId(currentFolderId));
            }
        }
    };

    const handleUploadFiles = (files: FileList) => {
        // Only upload if we're inside a container folder
        if (!currentFolderId) return;
        const container = containerService.getById(currentFolderId);
        if (!container) return;

        Array.from(files).forEach((file) => {
            // Detect document type from file name
            const fileName = file.name.toLowerCase();
            let documentType: import("@/types/container").DocumentType = "unknown";
            if (fileName.includes("bill") || fileName.includes("bl")) {
                documentType = "bill_of_lading";
            } else if (fileName.includes("invoice")) {
                documentType = "invoice";
            } else if (fileName.includes("phytosanitary")) {
                documentType = "phytosanitary_certificate";
            } else if (fileName.includes("movement")) {
                documentType = "movement_certificate";
            } else if (fileName.includes("packing")) {
                documentType = "packing_list";
            } else if (fileName.includes("fumigation")) {
                documentType = "fumigation_certificate";
            } else if (fileName.includes("sad") || fileName.includes("icm")) {
                documentType = "sad_icm";
            } else if (fileName.includes("coo") || fileName.includes("certificate of origin")) {
                documentType = "coo";
            }

            // Create document with required fields
            documentService.create({
                containerId: currentFolderId,
                type: documentType,
                name: file.name,
                extractedData: {},
                missingFields: [],
                status: "Pending",
            });
        });

        // Refresh documents
        const containerDocs = documentService.getByContainerId(currentFolderId) as DocumentFile[];
        setDocuments(containerDocs);
    };

    const getAvailableFoldersForMove = (): Array<{ id: string | null; label: string }> => {
        const allFolders = customFolderService.getAll();
        const currentFolderId = selectedCustomFolder?.id;

        // Filtra la cartella corrente e le sue sottocartelle (per evitare loop)
        const availableFolders = allFolders.filter(f => {
            if (f.id === currentFolderId) return false;
            // Verifica se è una sottocartella della cartella corrente
            const path = customFolderService.getFolderPath(f.id);
            return !path.some(p => p.id === currentFolderId);
        });

        const options: Array<{ id: string | null; label: string }> = [
            { id: null, label: t("drive.folder.move.root") },
            ...availableFolders.map(f => ({ id: f.id, label: f.name })),
        ];

        return options;
    };

    // Update URL when tab/view changes
    useEffect(() => {
        const params = new URLSearchParams();
        params.set("tab", activeTab);
        params.set("view", viewMode);
        if (currentFolderId) {
            params.set("folder", currentFolderId);
        }
        setSearchParams(params, { replace: true });
    }, [activeTab, viewMode, currentFolderId, setSearchParams]);

    // Filter empty folders if needed
    const filteredContainers = showEmptyFolders
        ? containers
        : containers.filter(c => documentService.getByContainerId(c.id).length > 0);

    const filteredCustomFolders = showEmptyFolders
        ? customFolders
        : customFolders.filter(f => {
            const totalDocs = f.containerIds.reduce((acc, containerId) => {
                return acc + documentService.getByContainerId(containerId).length;
            }, 0);
            return totalDocs > 0;
        });

    const displayItems = isSearching
        ? {
            containers: searchResults
                .filter(r => r.type === "container")
                .map(r => r.item as Container),
            customFolders: searchResults
                .filter(r => r.type === "custom_folder")
                .map(r => r.item as CustomFolder),
            documents: searchResults
                .filter(r => r.type === "document")
                .map(r => r.item as DocumentFile),
            unmappedFiles: searchResults
                .filter(r => r.type === "unmapped_file")
                .map(r => r.item as UnmappedFile),
        }
        : {
            containers: filteredContainers,
            customFolders: filteredCustomFolders,
            documents,
            unmappedFiles: activeTab === "unmapped" ? unmappedFiles : [],
        };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between gap-6">
                <div>
                    <h1 className="text-2xl font-bold text-primary">{t("drive.title")}</h1>
                </div>
                {/* Search */}
                <div className="relative flex-1">
                    <SearchLg className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 size-5 text-tertiary" />
                    <Input
                        placeholder={t("drive.search.placeholder")}
                        value={searchQuery}
                        onChange={setSearchQuery}
                        onKeyDown={handleSearchKeyDown}
                        className="pl-11"
                    />
                </div>
            </div>

            {/* Tabs */}
            <Tabs
                selectedKey={activeTab}
                onSelectionChange={(key) => {
                    setActiveTab(key as "myDrive" | "unmapped");
                    setCurrentFolderId(null);
                    setIsSearching(false);
                    setSearchQuery("");
                }}
            >
                <Tabs.List
                    type="button-border"
                    items={[
                        { id: "myDrive", label: t("drive.myDrive") },
                        { id: "unmapped", label: t("drive.unmappedFiles") },
                    ]}
                >
                    {(item) => (
                        <Tabs.Item key={item.id} id={item.id}>
                            {item.label}
                        </Tabs.Item>
                    )}
                </Tabs.List>

                {/* My Drive Tab */}
                <Tabs.Panel id="myDrive">
                    <div className="space-y-4 mt-6">
                        <div className="flex items-center justify-between gap-6">
                            <div className="flex items-center gap-4">
                                <h2 className="text-lg font-semibold text-primary">{t("drive.myDrive")}</h2>
                                {/* Mostra pulsante solo se non siamo dentro una cartella container e non stiamo cercando */}
                                {!isSearching && (!currentFolderId || !containerService.getById(currentFolderId)) ? (
                                    <Button
                                        color="secondary"
                                        size="sm"
                                        onClick={() => {
                                            setNewFolderName("");
                                            setSelectedParentFolderId(currentFolderId);
                                            setShowCreateFolderModal(true);
                                        }}
                                        iconLeading={<Plus className="size-4" />}
                                    >
                                        {t("drive.folder.create")}
                                    </Button>
                                ) : null}
                            </div>
                            {/* Breadcrumb */}
                            {breadcrumbs.length > 0 && (
                                <div className="flex items-center gap-2 text-sm">
                                    {breadcrumbs.length <= 2 ? (
                                        // Mostra tutti gli elementi se <= 2 livelli
                                        breadcrumbs.map((item, index) => (
                                            <div key={item.id} className="flex items-center gap-2">
                                                {index > 0 && <ChevronRight className="size-4 text-tertiary" />}
                                                <button
                                                    onClick={() => handleBreadcrumbClick(item)}
                                                    className={`hover:text-primary transition-colors ${index === breadcrumbs.length - 1
                                                        ? "text-primary font-medium"
                                                        : "text-tertiary cursor-pointer"
                                                        }`}
                                                >
                                                    {item.type === "root" ? t("drive.breadcrumb.home") : item.name}
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        // Mostra My Drive + ellipsis dropdown + cartella corrente se > 2 livelli
                                        <>
                                            {/* My Drive (primo elemento) */}
                                            <button
                                                onClick={() => handleBreadcrumbClick(breadcrumbs[0])}
                                                className="text-tertiary cursor-pointer hover:text-primary transition-colors"
                                            >
                                                {breadcrumbs[0].type === "root" ? t("drive.breadcrumb.home") : breadcrumbs[0].name}
                                            </button>

                                            {/* Separatore */}
                                            <ChevronRight className="size-4 text-tertiary" />

                                            {/* Dropdown ellipsis con elementi intermedi */}
                                            {breadcrumbs.slice(1, -1).length > 0 && (
                                                <Dropdown.Root>
                                                    <Dropdown.DotsHorizontalButton
                                                        className="text-tertiary hover:text-primary"
                                                        aria-label="Show intermediate folders"
                                                    />
                                                    <Dropdown.Popover placement="bottom start" className="min-w-[200px]">
                                                        <Dropdown.Menu
                                                            onAction={(key) => {
                                                                const item = breadcrumbs.find(b => b.id === key as string);
                                                                if (item) {
                                                                    handleBreadcrumbClick(item);
                                                                }
                                                            }}
                                                        >
                                                            {breadcrumbs.slice(1, -1).map((item) => (
                                                                <Dropdown.Item
                                                                    key={item.id}
                                                                    id={item.id}
                                                                    label={item.name}
                                                                    icon={Folder}
                                                                />
                                                            ))}
                                                        </Dropdown.Menu>
                                                    </Dropdown.Popover>
                                                </Dropdown.Root>
                                            )}

                                            {/* Separatore */}
                                            <ChevronRight className="size-4 text-tertiary" />

                                            {/* Cartella corrente (ultimo elemento) */}
                                            <button
                                                onClick={() => handleBreadcrumbClick(breadcrumbs[breadcrumbs.length - 1])}
                                                className="text-primary font-medium cursor-pointer hover:text-primary transition-colors"
                                            >
                                                {breadcrumbs[breadcrumbs.length - 1].type === "root"
                                                    ? t("drive.breadcrumb.home")
                                                    : breadcrumbs[breadcrumbs.length - 1].name}
                                            </button>
                                        </>
                                    )}
                                </div>
                            )}
                            <div className="flex items-center gap-4">
                                <Toggle
                                    size="sm"
                                    label={t("drive.showEmptyFolders")}
                                    isSelected={showEmptyFolders}
                                    onChange={(isSelected) => setShowEmptyFolders(isSelected)}
                                />
                                <div className="flex items-center gap-2">
                                    <Button
                                        color={viewMode === "card" ? "primary" : "secondary"}
                                        size="sm"
                                        onClick={() => setViewMode("card")}
                                        aria-label={t("drive.view.card")}
                                    >
                                        <LayoutGrid01 className="size-4" />
                                    </Button>
                                    <Button
                                        color={viewMode === "list" ? "primary" : "secondary"}
                                        size="sm"
                                        onClick={() => setViewMode("list")}
                                        aria-label={t("drive.view.list")}
                                    >
                                        <List className="size-4" />
                                    </Button>
                                </div>
                            </div>
                        </div>

                        {/* Upload Zone - Only show when inside a container folder */}
                        {!isSearching && currentFolderId && containerService.getById(currentFolderId) && (
                            <FileUploadDropZone
                                hint={t("drive.upload.dropZone")}
                                onDropFiles={handleUploadFiles}
                                allowsMultiple={true}
                            />
                        )}

                        {/* Files */}
                        {isSearching ? (
                            <div>
                                <h2 className="text-lg font-semibold text-primary mb-4">
                                    {t("drive.search.results")}
                                </h2>
                                {searchResults.length === 0 ? (
                                    <p className="text-tertiary text-center py-12">
                                        {t("drive.search.noResults")}
                                    </p>
                                ) : (
                                    <DocumentList
                                        containers={displayItems.containers}
                                        customFolders={displayItems.customFolders}
                                        documents={displayItems.documents}
                                        viewMode={viewMode}
                                        onContainerClick={handleContainerClick}
                                        onContainerContextMenu={handleContainerContextMenu}
                                        onCustomFolderClick={handleCustomFolderClick}
                                        onCustomFolderContextMenu={handleCustomFolderContextMenu}
                                        onDocumentClick={handleDocumentClick}
                                        onDocumentContextMenu={handleDocumentContextMenu}
                                    />
                                )}
                            </div>
                        ) : (
                            <div onContextMenu={handleGenericContextMenu}>
                                <DocumentList
                                    containers={displayItems.containers}
                                    customFolders={displayItems.customFolders}
                                    documents={displayItems.documents}
                                    viewMode={viewMode}
                                    onContainerClick={handleContainerClick}
                                    onContainerContextMenu={handleContainerContextMenu}
                                    onCustomFolderClick={handleCustomFolderClick}
                                    onCustomFolderContextMenu={handleCustomFolderContextMenu}
                                    onDocumentClick={handleDocumentClick}
                                    onDocumentContextMenu={handleDocumentContextMenu}
                                />
                            </div>
                        )}
                    </div>
                </Tabs.Panel>

                {/* Unmapped Files Tab */}
                <Tabs.Panel id="unmapped">
                    <div className="space-y-4 mt-6">
                        <div className="flex items-center justify-end gap-2">
                            <Button
                                color={viewMode === "card" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => setViewMode("card")}
                                aria-label={t("drive.view.card")}
                            >
                                <LayoutGrid01 className="size-4" />
                            </Button>
                            <Button
                                color={viewMode === "list" ? "primary" : "secondary"}
                                size="sm"
                                onClick={() => setViewMode("list")}
                                aria-label={t("drive.view.list")}
                            >
                                <List className="size-4" />
                            </Button>
                        </div>

                        {/* Files */}
                        <UnmappedFilesList
                            files={displayItems.unmappedFiles}
                            viewMode={viewMode}
                            onFileAssociate={() => {
                                // Refresh unmapped files
                                setUnmappedFiles(documentService.getUnmappedFiles());
                            }}
                        />
                    </div>
                </Tabs.Panel>
            </Tabs>

            {/* Context Menu */}
            <ContextMenu
                isOpen={contextMenu.isOpen}
                onClose={closeContextMenu}
                position={contextMenu.position}
                items={contextMenu.items}
            />

            {/* Rename Modal */}
            {showRenameModal && (
                <ModalOverlay isOpen={showRenameModal} onOpenChange={setShowRenameModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {selectedDocument
                                    ? t("drive.document.rename.title")
                                    : t("drive.folder.rename")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={selectedDocument ? t("drive.document.rename.label") : t("drive.folder.rename")}
                                    value={renameValue}
                                    onChange={setRenameValue}
                                />
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowRenameModal(false);
                                        setSelectedDocument(null);
                                        setSelectedCustomFolder(null);
                                        setRenameValue("");
                                    }}>
                                        {selectedDocument ? t("drive.document.rename.cancel") : t("drive.document.rename.cancel")}
                                    </Button>
                                    <Button onClick={handleRename}>
                                        {selectedDocument ? t("drive.document.rename.save") : t("drive.document.rename.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <ModalOverlay isOpen={showDeleteModal} onOpenChange={setShowDeleteModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {selectedDocument
                                    ? t("drive.document.delete.title")
                                    : t("drive.folder.delete")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">
                                    {selectedDocument
                                        ? t("drive.document.delete.message")
                                        : t("drive.folder.cannotDelete")}
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowDeleteModal(false);
                                        setSelectedDocument(null);
                                        setSelectedCustomFolder(null);
                                    }}>
                                        {selectedDocument ? t("drive.document.delete.cancel") : t("drive.document.delete.cancel")}
                                    </Button>
                                    <Button color="primary-destructive" onClick={handleDelete}>
                                        {selectedDocument ? t("drive.document.delete.confirm") : t("drive.folder.delete")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Move Modal */}
            {showMoveModal && selectedDocument && (
                <ModalOverlay isOpen={showMoveModal} onOpenChange={setShowMoveModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.move.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Select
                                    label={t("drive.document.move.label")}
                                    selectedKey={selectedContainerId}
                                    onSelectionChange={(key) => setSelectedContainerId(key as string)}
                                    items={containerService.getAll().map(c => ({ id: c.id, label: c.containerNumber }))}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowMoveModal(false);
                                        setSelectedDocument(null);
                                        setSelectedContainerId("");
                                    }}>
                                        {t("drive.document.move.cancel")}
                                    </Button>
                                    <Button onClick={handleMove}>
                                        {t("drive.document.move.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Share Modal */}
            {showShareModal && selectedDocument && (
                <ModalOverlay isOpen={showShareModal} onOpenChange={setShowShareModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.document.share.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={t("drive.document.share.email")}
                                    type="email"
                                    placeholder="email@example.com"
                                />
                                <Select
                                    label={t("drive.document.share.permission")}
                                    selectedKey="view"
                                    items={[
                                        { id: "view", label: t("drive.document.share.view") },
                                        { id: "edit", label: t("drive.document.share.edit") },
                                        { id: "comment", label: t("drive.document.share.comment") },
                                    ]}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowShareModal(false);
                                        setSelectedDocument(null);
                                    }}>
                                        {t("drive.document.share.cancel")}
                                    </Button>
                                    <Button onClick={handleShare}>
                                        {t("drive.document.share.send")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Download Modal */}
            {showDownloadModal && selectedDocument && (
                <ModalOverlay isOpen={showDownloadModal} onOpenChange={setShowDownloadModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.detail.downloadConfirm.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <p className="text-tertiary">
                                    {t("drive.detail.downloadConfirm.message")}
                                </p>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowDownloadModal(false);
                                        setSelectedDocument(null);
                                    }}>
                                        {t("drive.detail.downloadConfirm.cancel")}
                                    </Button>
                                    <Button onClick={handleDownload}>
                                        {t("drive.detail.downloadConfirm.download")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Move Folder Modal */}
            {showMoveFolderModal && (selectedCustomFolder || selectedContainer) && (
                <ModalOverlay isOpen={showMoveFolderModal} onOpenChange={setShowMoveFolderModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-lg w-full mx-auto flex flex-col">
                            <div className="flex flex-col gap-4 w-full">
                                <h2 className="text-lg font-semibold text-primary mb-4">
                                    {t("drive.folder.move.title")}
                                </h2>
                                <div className="space-y-4 w-full">
                                    <Select
                                        label={t("drive.folder.move.label")}
                                        selectedKey={selectedParentFolderId === null ? "root" : selectedParentFolderId}
                                        onSelectionChange={(key) => setSelectedParentFolderId(key === "root" ? null : key as string)}
                                        items={getAvailableFoldersForMove().map(f => ({ id: f.id === null ? "root" : f.id, label: f.label }))}
                                    >
                                        {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                    </Select>
                                    <div className="flex justify-end gap-2">
                                        <Button color="secondary" onClick={() => {
                                            setShowMoveFolderModal(false);
                                            setSelectedCustomFolder(null);
                                            setSelectedContainer(null);
                                            setSelectedParentFolderId(null);
                                        }}>
                                            {t("drive.folder.move.cancel")}
                                        </Button>
                                        <Button onClick={handleMoveFolder}>
                                            {t("drive.folder.move.save")}
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}

            {/* Create Folder Modal */}
            {showCreateFolderModal && (
                <ModalOverlay isOpen={showCreateFolderModal} onOpenChange={setShowCreateFolderModal}>
                    <Modal>
                        <Dialog className="rounded-xl bg-primary p-6 shadow-lg ring-1 ring-secondary max-w-md w-full mx-auto flex flex-col">
                            <h2 className="text-lg font-semibold text-primary mb-4">
                                {t("drive.folder.createModal.title")}
                            </h2>
                            <div className="space-y-4 w-full">
                                <Input
                                    label={t("drive.folder.createModal.label")}
                                    value={newFolderName}
                                    onChange={setNewFolderName}
                                />
                                <Select
                                    label={t("drive.folder.createModal.parentLabel")}
                                    selectedKey={selectedParentFolderId === null ? "root" : selectedParentFolderId}
                                    onSelectionChange={(key) => setSelectedParentFolderId(key === "root" ? null : key as string)}
                                    items={[
                                        { id: "root", label: t("drive.folder.move.root") },
                                        ...customFolderService.getAll()
                                            .filter(f => f.id !== selectedParentFolderId)
                                            .map(f => ({ id: f.id, label: f.name }))
                                    ]}
                                >
                                    {(item) => <Select.Item id={item.id}>{item.label}</Select.Item>}
                                </Select>
                                <div className="flex justify-end gap-2">
                                    <Button color="secondary" onClick={() => {
                                        setShowCreateFolderModal(false);
                                        setNewFolderName("");
                                        setSelectedParentFolderId(null);
                                    }}>
                                        {t("drive.folder.createModal.cancel")}
                                    </Button>
                                    <Button onClick={handleCreateFolder}>
                                        {t("drive.folder.createModal.save")}
                                    </Button>
                                </div>
                            </div>
                        </Dialog>
                    </Modal>
                </ModalOverlay>
            )}
        </div>
    );
};

